import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight, Pencil, Save, RotateCcw, X, Plus, Trash2, Lock,
  Leaf, Wind, Activity, Check, Menu, ChevronDown, Mail, MapPin, Phone,
  Heart, Droplet, Flame, Scale, TrendingUp, LineChart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const SLUG = "longevity-academy";

// ==== DESIGN TOKENS ====
const T_STYLE = {
  cream: "#F5F1EB",
  forest: "#0F0F0F",
  gold: "#EC6B1F",
  goldSoft: "#F5842B",
  ink: "#0F0F0F",
  softInk: "#4a4a4a",
  line: "#E8E2D5",
};

const serif = { fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" };
const sans = { fontFamily: "'Inter', -apple-system, sans-serif" };

// ==== LOGO SVG minimalista: infinito + foglia ====
const LongevityLogo = ({ color = T_STYLE.forest, size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 22 C24 22 20 30 20 36 C20 42 24 46 28 46 C34 46 38 40 44 32 C48 26 52 22 56 22" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    <path d="M32 42 C40 42 44 34 44 28 C44 22 40 18 36 18 C30 18 26 24 20 32 C16 38 12 42 8 42" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    <path d="M42 26 Q46 22 50 24 Q48 30 44 32 Z" fill={color} opacity="0.85" />
  </svg>
);

// Inline editable text
const T = ({ editing, value, onChange, as = "span", className = "", multiline = false, style = {}, placeholder = "" }) => {
  if (!editing) {
    const Tag = as;
    return <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: (value || "").replace(/\n/g, "<br/>") }} />;
  }
  if (multiline) {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-amber-500`} style={style} rows={3} placeholder={placeholder} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-amber-500`} style={style} placeholder={placeholder} />;
};

const LongevityAcademyPage = () => {
  const { user, token } = useAuth();
  const isAdmin = user && ["admin", "direzione"].includes(user.role);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const heroSlides = (data?.hero?.slides && data.hero.slides.length > 0)
    ? data.hero.slides
    : (data?.hero?.image ? [data.hero.image] : []);
  useEffect(() => {
    if (heroSlides.length < 2) return;
    const id = setInterval(() => setSlideIdx((i) => (i + 1) % heroSlides.length), 5000);
    return () => clearInterval(id);
  }, [heroSlides.length]);

  useEffect(() => {
    axios.get(`${API}/landing/${SLUG}`).then((r) => setData(r.data)).catch(() => toast.error("Errore caricamento"));
  }, []);

  const set = (path, val) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (k.includes("[")) {
          const [name, idx] = k.split(/[\[\]]/).filter(Boolean);
          ref = ref[name][idx];
        } else {
          ref = ref[k];
        }
      }
      const last = keys[keys.length - 1];
      if (last.includes("[")) {
        const [name, idx] = last.split(/[\[\]]/).filter(Boolean);
        ref[name][idx] = val;
      } else {
        ref[last] = val;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing/${SLUG}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Salvato");
      setEditing(false);
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare i contenuti di default?")) return;
    try {
      const r = await axios.post(`${API}/landing/${SLUG}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data); toast.success("Ripristinato"); setEditing(false);
    } catch { toast.error("Errore ripristino"); }
  };

  const uploadImage = async (fieldPath) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      const img = new Image(); const reader = new FileReader();
      reader.onload = (ev) => { img.src = ev.target.result; };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSide = 1800;
        let { width, height } = img;
        if (Math.max(width, height) > maxSide) {
          if (width >= height) { height = Math.round(height * (maxSide / width)); width = maxSide; }
          else { width = Math.round(width * (maxSide / height)); height = maxSide; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        set(fieldPath, canvas.toDataURL("image/jpeg", 0.85));
        toast.success("Immagine caricata");
      };
      reader.readAsDataURL(f);
    };
    inp.click();
  };

  const copyLink = () => {
    const url = window.location.origin + "/longevity-academy";
    navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"));
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T_STYLE.cream, ...sans }}>Caricamento…</div>;

  const b = data.brand || {};
  const h = data.hero || {};
  const ac = data.academy || {};
  const pil = data.pilastri || { items: [] };
  const auth = data.autorevolezza || { stats: [] };
  const perc = data.percorso || { livelli: [] };
  const tm = data.team || { istruttori: [] };
  const sh = data.shop || { prodotti: [] };
  const rt = data.retreat || {};
  const cta = data.cta_finale || {};
  const ct = data.contatti || {};
  const ft = data.footer || { links: [] };
  const lab = data.laboratorio || { servizi: [] };
  const pr = data.progressi || { metriche: [], grafico_energia: { punti: [], labels: [] } };
  const LAB_ICONS = { wind: Wind, scale: Scale, heart: Heart, activity: Activity, droplet: Droplet, flame: Flame };

  return (
    <div style={{ backgroundColor: T_STYLE.cream, color: T_STYLE.ink, ...sans }} data-testid="longevity-page">
      {/* ADMIN TOOLBAR */}
      {isAdmin && (
        <div className="sticky top-0 z-[70] px-4 py-2 flex items-center justify-between flex-wrap gap-2" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream, borderBottom: `2px solid ${T_STYLE.gold}` }} data-testid="admin-toolbar">
          <div className="flex items-center gap-2 text-xs">
            <span style={{ color: T_STYLE.gold }} className="font-semibold tracking-wider uppercase">Admin</span>
            <span className="opacity-70">Longevity Academy</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={copyLink} className="text-slate-900" data-testid="btn-copy-link">Copia link pubblico</Button>
            {!editing ? (
              <Button size="sm" onClick={() => setEditing(true)} style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest }} data-testid="btn-edit"><Pencil className="w-4 h-4 mr-1" /> Modifica</Button>
            ) : (
              <>
                <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save"><Save className="w-4 h-4 mr-1" /> {saving ? "Salvo…" : "Salva"}</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-slate-900" data-testid="btn-cancel"><X className="w-4 h-4 mr-1" /> Annulla</Button>
                <Button size="sm" variant="outline" onClick={resetDefault} className="text-red-600" data-testid="btn-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reset default</Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* NAV */}
      <nav className="absolute top-0 left-0 right-0 z-40 px-6 md:px-14 py-6 flex items-center justify-between" style={{ zIndex: 40 }}>
        {h.mode === "banner" ? (
          <span aria-hidden="true" />
        ) : (
          <a href="#top" className="flex items-center gap-3" data-testid="brand-link">
            <LongevityLogo color={T_STYLE.cream} size={38} />
            <div className="text-white leading-tight">
              <div className="text-xs tracking-[6px] uppercase opacity-80" style={sans}>
                {editing ? <Input value={b.sublabel} onChange={(e) => set("brand.sublabel", e.target.value)} className="bg-white/90 text-slate-900 text-xs h-6 w-20" /> : b.sublabel}
              </div>
              <div className="text-lg" style={serif}>
                {editing ? <Input value={b.name} onChange={(e) => set("brand.name", e.target.value)} className="bg-white/90 text-slate-900 h-7" /> : b.name}
              </div>
            </div>
          </a>
        )}
        <div className="hidden md:flex items-center gap-8">
          {(data.nav || []).map((n, i) => (
            <a key={i} href={n.href}
               className="text-sm tracking-wide font-semibold transition-colors"
               style={{ color: h.mode === "banner" ? T_STYLE.gold : "rgba(255,255,255,0.9)", ...sans }}>
              {n.label}
            </a>
          ))}
        </div>
        <button onClick={() => setNavOpen(v => !v)} className="md:hidden" style={{ color: h.mode === "banner" ? T_STYLE.gold : "#fff" }} data-testid="mobile-menu"><Menu className="w-6 h-6" /></button>
        {navOpen && (
          <div className="absolute top-full left-0 right-0 md:hidden backdrop-blur-md py-4 px-6" style={{ backgroundColor: `${T_STYLE.forest}f5` }}>
            {(data.nav || []).map((n, i) => (
              <a key={i} href={n.href} onClick={() => setNavOpen(false)} className="block py-2 text-sm font-semibold" style={{ color: T_STYLE.goldSoft, ...sans }}>{n.label}</a>
            ))}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="top" className="relative w-full overflow-hidden" data-testid="hero" style={{ backgroundColor: T_STYLE.cream }}>
        {h.mode === "banner" ? (
          <>
            {/* BANNER MODE — carosello di immagini brandizzate */}
            <div className="relative w-full" data-testid="hero-carousel">
              {heroSlides.map((src, i) => (
                <img key={i} src={src} alt={`Longevity Academy slide ${i+1}`}
                     className={`w-full h-auto block transition-opacity duration-700 ${i === slideIdx ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}
                     data-testid={i === 0 ? "hero-banner-image" : `hero-slide-${i}`} />
              ))}
              {editing && (
                <div className="absolute top-4 right-4 z-30 flex flex-col gap-2 items-end">
                  <button onClick={() => uploadImage(`hero.slides[${slideIdx}]`)} className="text-xs px-3 py-2 rounded-full font-semibold shadow-lg" style={{ backgroundColor: T_STYLE.gold, color: "#fff" }}>Cambia slide corrente</button>
                  <button onClick={() => { const next = [...heroSlides, heroSlides[0] || ""]; set("hero.slides", next); toast.success("Slide aggiunta — modificala"); }} className="text-xs px-3 py-2 rounded-full font-semibold shadow-lg" style={{ backgroundColor: "#fff", color: T_STYLE.forest }}>+ Aggiungi slide</button>
                  {heroSlides.length > 1 && (
                    <button onClick={() => { const next = heroSlides.filter((_,i) => i !== slideIdx); set("hero.slides", next); setSlideIdx(0); }} className="text-xs px-3 py-2 rounded-full font-semibold shadow-lg" style={{ backgroundColor: "#e11d48", color: "#fff" }}>Elimina slide</button>
                  )}
                </div>
              )}
              {heroSlides.length > 1 && (
                <div className="absolute bottom-4 md:bottom-6 left-0 right-0 flex justify-center gap-2 z-20" data-testid="hero-dots">
                  {heroSlides.map((_, i) => (
                    <button key={i} onClick={() => setSlideIdx(i)} aria-label={`slide ${i+1}`}
                            className="transition-all"
                            style={{
                              width: i === slideIdx ? 32 : 10,
                              height: 4,
                              backgroundColor: i === slideIdx ? T_STYLE.gold : "rgba(255,255,255,0.5)",
                              borderRadius: 2,
                            }} />
                  ))}
                </div>
              )}
            </div>
            <div className="px-4 md:px-14 lg:px-24 py-8 md:py-14" style={{ backgroundColor: T_STYLE.cream, borderTop: `3px solid ${T_STYLE.gold}` }}>
              <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-4 items-stretch md:items-center justify-between">
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 md:gap-3">
                  <a href={h.cta_primary_href} className="inline-flex items-center justify-center gap-2 px-5 md:px-7 py-3 md:py-3.5 text-[11px] md:text-xs tracking-[2px] uppercase font-bold transition-all hover:scale-[1.02] shadow-xl"
                     style={{ backgroundColor: T_STYLE.gold, color: "#fff", ...sans }} data-testid="hero-cta-primary">
                    <T editing={editing} value={h.cta_primary} onChange={(v) => set("hero.cta_primary", v)} as="span" /> <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={h.cta_secondary_href} className="inline-flex items-center justify-center gap-2 px-5 md:px-7 py-3 md:py-3.5 text-[11px] md:text-xs tracking-[2px] uppercase font-bold border-2 transition-colors hover:bg-black/5"
                     style={{ borderColor: T_STYLE.forest, color: T_STYLE.forest, ...sans }} data-testid="hero-cta-secondary">
                    <T editing={editing} value={h.cta_secondary} onChange={(v) => set("hero.cta_secondary", v)} as="span" />
                  </a>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 md:gap-3" data-testid="programmi-cta-row">
                  <a href="/longevity/aziende"
                     className="group inline-flex items-center justify-center gap-2 px-5 md:px-6 py-3 md:py-3.5 text-[11px] md:text-xs tracking-[1.5px] uppercase font-bold transition-all hover:scale-[1.02] shadow-xl"
                     style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.goldSoft, border: `1.5px solid ${T_STYLE.gold}`, ...sans }}
                     data-testid="btn-programma-aziende">
                    🏢 <span>Aziende & Affiliazioni</span> <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </a>
                  <a href="/longevity/individuale"
                     className="group inline-flex items-center justify-center gap-2 px-5 md:px-6 py-3 md:py-3.5 text-[11px] md:text-xs tracking-[1.5px] uppercase font-bold transition-all hover:scale-[1.02] shadow-xl"
                     style={{ backgroundColor: "#fff", color: T_STYLE.forest, border: `1.5px solid ${T_STYLE.gold}`, ...sans }}
                     data-testid="btn-programma-individuale">
                    👤 <span>Cliente Individuale</span> <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
              {editing && (
                <div className="max-w-6xl mx-auto mt-6 pt-6 border-t border-black/10 space-y-3">
                  <p className="text-xs uppercase tracking-widest" style={{ color: T_STYLE.gold }}>Testi hero (visibili solo in modalità classica)</p>
                  <T editing={editing} value={h.eyebrow} onChange={(v) => set("hero.eyebrow", v)} className="text-slate-700 text-xs" />
                  <T editing={editing} value={h.headline_line1} onChange={(v) => set("hero.headline_line1", v)} className="text-slate-700" />
                  <T editing={editing} value={h.headline_line2} onChange={(v) => set("hero.headline_line2", v)} className="text-slate-700" />
                  <T editing={editing} value={h.subtitle} onChange={(v) => set("hero.subtitle", v)} multiline className="text-slate-700" />
                  <div className="flex gap-2 items-center text-xs text-slate-600 pt-2">
                    <span>Modalità hero:</span>
                    <button type="button" onClick={() => set("hero.mode", "classic")} className="px-3 py-1 border border-black/20 hover:bg-black/5 rounded">Torna alla modalità classica</button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="relative h-screen min-h-[720px] w-full overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${h.image})` }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${T_STYLE.forest}cc 0%, ${T_STYLE.forest}88 40%, ${T_STYLE.forest}ee 100%)` }} />
            {editing && (
              <>
                <button onClick={() => uploadImage("hero.image")} className="absolute top-4 right-4 z-30 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: "#fff" }}>Cambia immagine hero</button>
                <button type="button" onClick={() => set("hero.mode", "banner")} className="absolute top-16 right-4 z-30 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: "#fff", color: T_STYLE.forest }}>Attiva banner mode</button>
              </>
            )}
            <div className="relative z-10 h-full flex items-center px-6 md:px-14 lg:px-24">
              <div className="max-w-4xl">
                <T editing={editing} value={h.eyebrow} onChange={(v) => set("hero.eyebrow", v)} as="p"
                   className="text-white/80 text-xs tracking-[8px] uppercase mb-8 block" style={sans} />
                <h1 className="text-white text-5xl md:text-7xl lg:text-8xl font-light leading-[1.02] mb-8" style={serif}>
                  <T editing={editing} value={h.headline_line1} onChange={(v) => set("hero.headline_line1", v)} as="span" />
                  <br />
                  <em style={{ color: T_STYLE.goldSoft, fontStyle: "italic", fontWeight: 400 }}>
                    <T editing={editing} value={h.headline_line2} onChange={(v) => set("hero.headline_line2", v)} as="span" />
                  </em>
                </h1>
                <T editing={editing} value={h.subtitle} onChange={(v) => set("hero.subtitle", v)} multiline as="p"
                   className="text-white/85 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl font-light block" style={sans} />
                <div className="flex flex-wrap gap-4">
                  <a href={h.cta_primary_href} className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02] shadow-2xl"
                     style={{ backgroundColor: T_STYLE.gold, color: "#fff", ...sans }} data-testid="hero-cta-primary">
                    <T editing={editing} value={h.cta_primary} onChange={(v) => set("hero.cta_primary", v)} as="span" /> <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={h.cta_secondary_href} className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold border transition-colors hover:bg-white/10"
                     style={{ borderColor: T_STYLE.cream, color: T_STYLE.cream, ...sans }} data-testid="hero-cta-secondary">
                    <T editing={editing} value={h.cta_secondary} onChange={(v) => set("hero.cta_secondary", v)} as="span" />
                  </a>
                </div>
                <div className="mt-8 pt-8 border-t border-white/20 flex flex-wrap gap-4" data-testid="programmi-cta-row">
                  <a href="/longevity/aziende"
                     className="group inline-flex items-center gap-3 px-7 py-4 rounded-sm text-sm tracking-[1.5px] uppercase font-semibold transition-all hover:scale-[1.02] shadow-xl backdrop-blur-sm"
                     style={{ backgroundColor: "rgba(15,15,15,0.85)", color: T_STYLE.goldSoft, border: `1px solid ${T_STYLE.gold}`, ...sans }}
                     data-testid="btn-programma-aziende">
                    🏢 <span>Aziende & Affiliazioni</span> <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                  <a href="/longevity/individuale"
                     className="group inline-flex items-center gap-3 px-7 py-4 rounded-sm text-sm tracking-[1.5px] uppercase font-semibold transition-all hover:scale-[1.02] shadow-xl backdrop-blur-sm"
                     style={{ backgroundColor: "rgba(245,241,235,0.9)", color: T_STYLE.forest, border: `1px solid ${T_STYLE.cream}`, ...sans }}
                     data-testid="btn-programma-individuale">
                    👤 <span>Cliente Individuale</span> <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 animate-bounce">
              <ChevronDown className="w-6 h-6" />
            </div>
          </div>
        )}
      </section>

      {/* URGENCY BANNER — Posti limitati */}
      <div className="w-full px-4 md:px-14 py-4 md:py-5 text-center relative overflow-hidden" style={{ backgroundColor: T_STYLE.gold, color: "#fff" }} data-testid="urgency-banner">
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-3 flex-wrap">
          <span className="animate-pulse text-lg">⚡</span>
          <p className="text-sm md:text-base font-bold uppercase tracking-[2px]" style={sans}>
            Affrettati a prenotare il tuo posto — Posti limitati
          </p>
          <a href="#contatti" className="inline-flex items-center gap-2 px-4 py-2 text-[11px] md:text-xs uppercase tracking-[2px] font-bold bg-white transition-transform hover:scale-105" style={{ color: T_STYLE.forest, ...sans }} data-testid="btn-prenota-urgency">
            Prenota ora <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* CTA VO2 MAX — link alla sezione scientifica dedicata */}
      <section className="py-12 md:py-16 px-4 md:px-14" style={{ backgroundColor: T_STYLE.forest }} data-testid="vo2max-cta">
        <div className="max-w-6xl mx-auto">
          <a href="/longevity/vo2max" className="group grid md:grid-cols-3 gap-6 md:gap-8 items-center p-6 md:p-12 border-l-4 hover:shadow-2xl transition-all" style={{ borderColor: T_STYLE.gold, backgroundColor: "#000" }} data-testid="vo2max-link-card">
            <div className="md:col-span-2">
              <p className="text-[10px] md:text-xs tracking-[4px] uppercase font-bold mb-3" style={{ color: T_STYLE.goldSoft }}>Sezione scientifica</p>
              <h3 className="text-white text-2xl md:text-5xl font-light leading-tight mb-4" style={serif}>
                Il metodo <em style={{ color: T_STYLE.goldSoft, fontStyle: "italic" }}>VO₂ MAX</em>: la scienza che allunga la vita
              </h3>
              <p className="text-white/70 text-sm md:text-lg leading-relaxed mb-6">
                Meta-analisi su <strong style={{ color: T_STYLE.goldSoft }}>3.813.484 persone</strong>: ogni +1 MET di fitness = −14% mortalità.
                Percorso di 12 settimane monitorato da remoto con AI. Nessun laboratorio.
              </p>
              <span className="inline-flex items-center gap-2 md:gap-3 px-5 md:px-7 py-3 md:py-3.5 text-xs md:text-sm tracking-[2px] uppercase font-bold group-hover:scale-105 transition-transform"
                    style={{ backgroundColor: T_STYLE.gold, color: "#fff", ...sans }}>
                Scopri la sezione scientifica <ArrowRight className="w-4 h-4" />
              </span>
            </div>
            <div className="text-center md:text-right">
              <div className="inline-flex flex-col items-center gap-1">
                <div className="text-5xl md:text-8xl font-bold leading-none" style={{ color: T_STYLE.goldSoft, ...serif }}>−14<span className="text-2xl md:text-3xl">%</span></div>
                <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/60">mortalità · per ogni +1 MET</div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* ACADEMY */}
      <section id="academy" className="py-32 px-6 md:px-14 lg:px-24" data-testid="academy">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={ac.image} alt="Academy" className="w-full h-[600px] object-cover shadow-2xl" />
            {editing && (
              <button onClick={() => uploadImage("academy.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest }}>Cambia immagine</button>
            )}
            <div className="absolute -bottom-6 -left-6 h-24 w-24" style={{ backgroundColor: T_STYLE.gold }} />
          </div>
          <div>
            <T editing={editing} value={ac.eyebrow} onChange={(v) => set("academy.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
            <h2 className="text-4xl md:text-5xl font-light leading-[1.1] mb-8" style={{ color: T_STYLE.forest, ...serif }}>
              <T editing={editing} value={ac.title_line1} onChange={(v) => set("academy.title_line1", v)} as="span" />
              <br />
              <em style={{ fontStyle: "italic", color: T_STYLE.softInk }}>
                <T editing={editing} value={ac.title_line2} onChange={(v) => set("academy.title_line2", v)} as="span" />
              </em>
            </h2>
            <div className="w-16 h-[2px] mb-8" style={{ backgroundColor: T_STYLE.gold }} />
            {(ac.paragraphs || []).map((p, i) => (
              <div key={i} className="mb-5">
                <T editing={editing} value={p} onChange={(v) => { const arr = [...ac.paragraphs]; arr[i] = v; set("academy.paragraphs", arr); }} multiline as="p"
                   className="text-lg leading-[1.9] font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILASTRI */}
      <section id="pilastri" className="py-32 px-6 md:px-14 lg:px-24" style={{ backgroundColor: "#ffffff" }} data-testid="pilastri">
        <div className="max-w-6xl mx-auto text-center mb-20">
          <T editing={editing} value={pil.eyebrow} onChange={(v) => set("pilastri.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
          <T editing={editing} value={pil.title} onChange={(v) => set("pilastri.title", v)} as="h2"
             className="text-4xl md:text-6xl font-light leading-tight mb-6 block" style={{ color: T_STYLE.forest, ...serif }} />
          <T editing={editing} value={pil.subtitle} onChange={(v) => set("pilastri.subtitle", v)} multiline as="p"
             className="text-lg max-w-2xl mx-auto font-light leading-relaxed block" style={{ color: T_STYLE.softInk, ...sans }} />
        </div>
        <div className="max-w-6xl mx-auto space-y-24">
          {(pil.items || []).map((it, i) => {
            const rev = i % 2 === 1;
            return (
              <div key={i} className={`grid md:grid-cols-2 gap-12 items-center ${rev ? "md:grid-flow-dense" : ""}`} data-testid={`pilastro-${i}`}>
                <div className={`relative ${rev ? "md:col-start-2" : ""}`}>
                  <img src={it.image} alt={it.titolo} className="w-full h-[520px] object-cover shadow-2xl" />
                  {editing && (
                    <button onClick={() => uploadImage(`pilastri.items[${i}].image`)} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest }}>Cambia immagine</button>
                  )}
                </div>
                <div className={rev ? "md:col-start-1 md:row-start-1" : ""}>
                  <div className="text-7xl font-light mb-4 opacity-30" style={{ color: T_STYLE.gold, ...serif }}>
                    {editing ? <Input value={it.numero} onChange={(e) => set(`pilastri.items[${i}].numero`, e.target.value)} className="w-20 text-4xl" /> : it.numero}
                  </div>
                  <T editing={editing} value={it.titolo} onChange={(v) => set(`pilastri.items[${i}].titolo`, v)} as="h3"
                     className="text-4xl md:text-5xl font-light mb-3 leading-tight block" style={{ color: T_STYLE.forest, ...serif }} />
                  <T editing={editing} value={it.sottotitolo} onChange={(v) => set(`pilastri.items[${i}].sottotitolo`, v)} as="p"
                     className="text-sm tracking-[2px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
                  <T editing={editing} value={it.descrizione} onChange={(v) => set(`pilastri.items[${i}].descrizione`, v)} multiline as="p"
                     className="text-lg leading-[1.9] mb-8 font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
                  <ul className="space-y-3">
                    {(it.punti || []).map((p, pi) => (
                      <li key={pi} className="flex items-start gap-3 text-base" style={{ color: T_STYLE.ink, ...sans }}>
                        <span style={{ color: T_STYLE.gold }} className="mt-1">◆</span>
                        {editing ? (
                          <div className="flex-1 flex gap-2">
                            <Input value={p} onChange={(e) => { const arr = [...it.punti]; arr[pi] = e.target.value; set(`pilastri.items[${i}].punti`, arr); }} className="text-sm border-amber-500 border-2" />
                            <button onClick={() => { const arr = it.punti.filter((_, ii) => ii !== pi); set(`pilastri.items[${i}].punti`, arr); }} className="text-red-500"><X className="w-4 h-4" /></button>
                          </div>
                        ) : <span>{p}</span>}
                      </li>
                    ))}
                    {editing && <li><button onClick={() => set(`pilastri.items[${i}].punti`, [...(it.punti || []), "Nuovo punto"])} className="text-xs flex items-center gap-1" style={{ color: T_STYLE.gold }}><Plus className="w-3 h-3" /> Aggiungi</button></li>}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* AUTOREVOLEZZA */}
      <section className="py-24 px-6 md:px-14 lg:px-24" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream }} data-testid="autorevolezza">
        <div className="max-w-5xl mx-auto text-center">
          <T editing={editing} value={auth.eyebrow} onChange={(v) => set("autorevolezza.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
          <T editing={editing} value={auth.title} onChange={(v) => set("autorevolezza.title", v)} as="h2"
             className="text-4xl md:text-5xl font-light leading-tight mb-6 block" style={serif} />
          <T editing={editing} value={auth.description} onChange={(v) => set("autorevolezza.description", v)} multiline as="p"
             className="text-lg opacity-85 max-w-3xl mx-auto mb-16 font-light block" style={sans} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {(auth.stats || []).map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-5xl md:text-6xl font-light mb-2" style={{ color: T_STYLE.gold, ...serif }}>
                  {editing ? <Input value={s.numero} onChange={(e) => { const arr = [...auth.stats]; arr[i] = { ...s, numero: e.target.value }; set("autorevolezza.stats", arr); }} className="text-center text-slate-900" /> : s.numero}
                </div>
                <div className="text-xs tracking-[3px] uppercase opacity-80" style={sans}>
                  {editing ? <Input value={s.label} onChange={(e) => { const arr = [...auth.stats]; arr[i] = { ...s, label: e.target.value }; set("autorevolezza.stats", arr); }} className="text-center text-slate-900" /> : s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERCORSO 3 LIVELLI */}
      <section id="percorso" className="py-32 px-6 md:px-14 lg:px-24" data-testid="percorso">
        <div className="max-w-6xl mx-auto text-center mb-20">
          <T editing={editing} value={perc.eyebrow} onChange={(v) => set("percorso.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
          <T editing={editing} value={perc.title} onChange={(v) => set("percorso.title", v)} as="h2"
             className="text-4xl md:text-6xl font-light leading-tight mb-6 block" style={{ color: T_STYLE.forest, ...serif }} />
          <T editing={editing} value={perc.subtitle} onChange={(v) => set("percorso.subtitle", v)} multiline as="p"
             className="text-lg max-w-2xl mx-auto font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
        </div>

        {/* Stepper verticale */}
        <div className="max-w-4xl mx-auto relative">
          <div className="absolute left-6 top-8 bottom-8 w-[2px] hidden md:block" style={{ backgroundColor: T_STYLE.line }} />
          <div className="space-y-6">
            {(perc.livelli || []).map((lv, i) => {
              const locked = i > 0; // in V1: solo primo sbloccato "in preview"; tutti "Prossimamente"
              return (
                <div key={i} className="relative pl-0 md:pl-20" data-testid={`livello-${i}`}>
                  {/* Checkbox stepper */}
                  <div className="absolute left-0 top-8 hidden md:flex items-center justify-center w-12 h-12 rounded-full border-2" style={{ backgroundColor: locked ? T_STYLE.cream : T_STYLE.gold, borderColor: T_STYLE.gold, color: locked ? T_STYLE.gold : T_STYLE.forest }}>
                    {locked ? <Lock className="w-4 h-4" /> : <Check className="w-5 h-5" />}
                  </div>
                  <div className="bg-white border shadow-md hover:shadow-xl transition-all duration-500 p-8 md:p-10" style={{ borderColor: T_STYLE.line, opacity: locked ? 0.7 : 1 }}>
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                      <div>
                        <div className="text-xs tracking-[4px] uppercase mb-2" style={{ color: T_STYLE.gold, ...sans }}>
                          Livello {editing ? <Input value={lv.numero} onChange={(e) => set(`percorso.livelli[${i}].numero`, e.target.value)} className="inline w-14 h-6" /> : lv.numero} · {editing ? <Input value={lv.codice} onChange={(e) => set(`percorso.livelli[${i}].codice`, e.target.value)} className="inline w-32 h-6" /> : lv.codice}
                        </div>
                        <T editing={editing} value={lv.titolo} onChange={(v) => set(`percorso.livelli[${i}].titolo`, v)} as="h3"
                           className="text-3xl md:text-4xl font-light leading-tight block mb-2" style={{ color: T_STYLE.forest, ...serif }} />
                        <div className="text-sm" style={{ color: T_STYLE.softInk, ...sans }}>
                          {editing ? <Input value={lv.durata} onChange={(e) => set(`percorso.livelli[${i}].durata`, e.target.value)} className="text-sm" /> : lv.durata}
                        </div>
                        {lv.richiede && !editing && (
                          <p className="text-xs mt-2 italic" style={{ color: T_STYLE.gold, ...sans }}>Richiede: {lv.richiede}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-4xl md:text-5xl font-light" style={{ color: T_STYLE.forest, ...serif }}>
                          {editing ? <Input value={lv.prezzo} onChange={(e) => set(`percorso.livelli[${i}].prezzo`, e.target.value)} className="text-2xl text-right w-36" /> : lv.prezzo}
                        </div>
                      </div>
                    </div>
                    <T editing={editing} value={lv.descrizione} onChange={(v) => set(`percorso.livelli[${i}].descrizione`, v)} multiline as="p"
                       className="text-base leading-relaxed mb-6 block" style={{ color: T_STYLE.softInk, ...sans }} />
                    <ul className="grid sm:grid-cols-2 gap-2 mb-8">
                      {(lv.include || []).map((v, ii) => (
                        <li key={ii} className="flex items-start gap-2 text-sm" style={{ color: T_STYLE.ink, ...sans }}>
                          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: T_STYLE.gold }} />
                          {editing ? (
                            <div className="flex-1 flex gap-1">
                              <Input value={v} onChange={(e) => { const arr = [...lv.include]; arr[ii] = e.target.value; set(`percorso.livelli[${i}].include`, arr); }} className="text-sm border-amber-500 border-2 h-7" />
                              <button onClick={() => { const arr = lv.include.filter((_, k) => k !== ii); set(`percorso.livelli[${i}].include`, arr); }} className="text-red-500"><X className="w-3 h-3" /></button>
                            </div>
                          ) : <span>{v}</span>}
                        </li>
                      ))}
                      {editing && <li><button onClick={() => set(`percorso.livelli[${i}].include`, [...(lv.include || []), "Nuovo"])} className="text-xs" style={{ color: T_STYLE.gold }}><Plus className="w-3 h-3 inline" /> Aggiungi</button></li>}
                    </ul>
                    <button disabled className="inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[2px] uppercase font-semibold cursor-not-allowed opacity-70" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream, ...sans }}>
                      <Lock className="w-3 h-3" />
                      {editing ? <Input value={lv.cta_label} onChange={(e) => set(`percorso.livelli[${i}].cta_label`, e.target.value)} className="bg-white text-slate-900 h-7" /> : (lv.cta_label || "Prossimamente")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section id="team" className="py-32 px-6 md:px-14 lg:px-24" style={{ backgroundColor: "#ffffff" }} data-testid="team">
        <div className="max-w-6xl mx-auto text-center mb-20">
          <T editing={editing} value={tm.eyebrow} onChange={(v) => set("team.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
          <T editing={editing} value={tm.title} onChange={(v) => set("team.title", v)} as="h2"
             className="text-4xl md:text-6xl font-light leading-tight mb-6 block" style={{ color: T_STYLE.forest, ...serif }} />
          <T editing={editing} value={tm.subtitle} onChange={(v) => set("team.subtitle", v)} multiline as="p"
             className="text-lg max-w-2xl mx-auto font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(tm.istruttori || []).map((p, i) => (
            <div key={i} className="group" data-testid={`istruttore-${i}`}>
              <div className="relative overflow-hidden mb-5" style={{ aspectRatio: "3/4", backgroundColor: T_STYLE.line }}>
                <img src={p.foto} alt={p.nome} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" style={{ filter: "sepia(15%) saturate(85%)" }} loading="lazy" />
                {editing && (
                  <button onClick={() => uploadImage(`team.istruttori[${i}].foto`)} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest }}>Cambia foto</button>
                )}
              </div>
              <T editing={editing} value={p.nome} onChange={(v) => set(`team.istruttori[${i}].nome`, v)} as="h3"
                 className="text-2xl font-normal mb-1 leading-tight block" style={{ color: T_STYLE.forest, ...serif }} />
              <T editing={editing} value={p.ruolo} onChange={(v) => set(`team.istruttori[${i}].ruolo`, v)} as="p"
                 className="text-xs tracking-[2px] uppercase mb-4 block" style={{ color: T_STYLE.gold, ...sans }} />
              <T editing={editing} value={p.bio} onChange={(v) => set(`team.istruttori[${i}].bio`, v)} multiline as="p"
                 className="text-sm leading-relaxed mb-4 font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
              <div className="flex flex-wrap gap-2">
                {(p.badges || []).map((b, bi) => (
                  editing ? (
                    <div key={bi} className="inline-flex items-center gap-1 border px-2 rounded-full" style={{ borderColor: T_STYLE.gold }}>
                      <Input value={b} onChange={(e) => { const arr = [...p.badges]; arr[bi] = e.target.value; set(`team.istruttori[${i}].badges`, arr); }} className="border-0 h-6 text-xs w-28" />
                      <button onClick={() => { const arr = p.badges.filter((_, k) => k !== bi); set(`team.istruttori[${i}].badges`, arr); }} className="text-red-500"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <span key={bi} className="inline-block text-xs px-3 py-1 border rounded-full" style={{ borderColor: T_STYLE.gold, color: T_STYLE.gold, ...sans }}>{b}</span>
                  )
                ))}
                {editing && <button onClick={() => set(`team.istruttori[${i}].badges`, [...(p.badges || []), "Nuovo"])} className="text-xs" style={{ color: T_STYLE.gold }}><Plus className="w-3 h-3 inline" /> +</button>}
              </div>
              {editing && (
                <button onClick={() => { const arr = tm.istruttori.filter((_, ii) => ii !== i); set("team.istruttori", arr); }} className="mt-3 text-xs text-red-500 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Rimuovi</button>
              )}
            </div>
          ))}
          {editing && (
            <button onClick={() => set("team.istruttori", [...(tm.istruttori || []), { nome: "Nuovo Istruttore", ruolo: "Ruolo", bio: "Biografia breve", badges: ["Badge 1"], foto: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80" }])} className="border-2 border-dashed rounded p-8 flex flex-col items-center justify-center min-h-[500px]" style={{ borderColor: T_STYLE.gold, color: T_STYLE.gold }}><Plus className="w-8 h-8 mb-2" /> Aggiungi istruttore</button>
          )}
        </div>
      </section>

      {/* LABORATORIO & MISURAZIONI */}
      <section id="laboratorio" className="py-32 px-6 md:px-14 lg:px-24" style={{ backgroundColor: T_STYLE.cream }} data-testid="laboratorio">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center mb-20">
          <div className="relative">
            <img src={lab.image} alt="Laboratorio" className="w-full h-[560px] object-cover shadow-2xl" />
            {editing && (
              <button onClick={() => uploadImage("laboratorio.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest }}>Cambia immagine</button>
            )}
            <div className="absolute -bottom-6 -right-6 h-24 w-24" style={{ backgroundColor: T_STYLE.gold }} />
          </div>
          <div>
            <T editing={editing} value={lab.eyebrow} onChange={(v) => set("laboratorio.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
            <h2 className="text-4xl md:text-6xl font-light leading-[1.05] mb-6" style={{ color: T_STYLE.forest, ...serif }}>
              <T editing={editing} value={lab.title_line1} onChange={(v) => set("laboratorio.title_line1", v)} as="span" />
              <br />
              <em style={{ color: T_STYLE.gold, fontStyle: "italic" }}>
                <T editing={editing} value={lab.title_line2} onChange={(v) => set("laboratorio.title_line2", v)} as="span" />
              </em>
            </h2>
            <div className="w-16 h-[2px] mb-6" style={{ backgroundColor: T_STYLE.gold }} />
            <T editing={editing} value={lab.subtitle} onChange={(v) => set("laboratorio.subtitle", v)} multiline as="p"
               className="text-lg leading-[1.9] font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
            <a href={lab.cta_href} className="inline-flex items-center gap-3 mt-8 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02]"
               style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream, ...sans }} data-testid="lab-cta">
              <T editing={editing} value={lab.cta_label} onChange={(v) => set("laboratorio.cta_label", v)} as="span" /> <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Griglia servizi laboratorio */}
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(lab.servizi || []).map((s, i) => {
            const Icon = LAB_ICONS[s.icon] || Activity;
            return (
              <div key={i} className="bg-white p-8 shadow-md hover:shadow-xl transition-all duration-500 border relative group" style={{ borderColor: T_STYLE.line }} data-testid={`lab-servizio-${i}`}>
                <div className="w-14 h-14 flex items-center justify-center mb-5" style={{ backgroundColor: `${T_STYLE.gold}20` }}>
                  <Icon className="w-7 h-7" strokeWidth={1.4} style={{ color: T_STYLE.gold }} />
                </div>
                <T editing={editing} value={s.nome} onChange={(v) => { const arr = [...lab.servizi]; arr[i] = { ...s, nome: v }; set("laboratorio.servizi", arr); }} as="h3"
                   className="text-xl font-normal mb-3 leading-tight block" style={{ color: T_STYLE.forest, ...serif }} />
                <T editing={editing} value={s.desc} onChange={(v) => { const arr = [...lab.servizi]; arr[i] = { ...s, desc: v }; set("laboratorio.servizi", arr); }} multiline as="p"
                   className="text-sm leading-relaxed mb-4 font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
                <div className="pt-3 border-t" style={{ borderColor: T_STYLE.line }}>
                  <span className="text-xs tracking-[2px] uppercase" style={{ color: T_STYLE.gold, ...sans }}>Unità di misura</span>
                  <div className="text-sm font-semibold mt-1" style={{ color: T_STYLE.forest, ...sans }}>
                    {editing ? <Input value={s.unita} onChange={(e) => { const arr = [...lab.servizi]; arr[i] = { ...s, unita: e.target.value }; set("laboratorio.servizi", arr); }} className="h-7 text-sm" /> : s.unita}
                  </div>
                </div>
                {editing && (
                  <div className="mt-3 space-y-2">
                    <select value={s.icon} onChange={(e) => { const arr = [...lab.servizi]; arr[i] = { ...s, icon: e.target.value }; set("laboratorio.servizi", arr); }} className="text-xs border rounded px-2 py-1 w-full">
                      {Object.keys(LAB_ICONS).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                    <button onClick={() => { const arr = lab.servizi.filter((_, k) => k !== i); set("laboratorio.servizi", arr); }} className="text-red-500 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Rimuovi</button>
                  </div>
                )}
              </div>
            );
          })}
          {editing && (
            <button onClick={() => set("laboratorio.servizi", [...(lab.servizi || []), { nome: "Nuovo servizio", desc: "Descrizione", unita: "u.m.", icon: "activity" }])} className="border-2 border-dashed p-8 flex flex-col items-center justify-center min-h-[280px]" style={{ borderColor: T_STYLE.gold, color: T_STYLE.gold }}><Plus className="w-8 h-8 mb-2" /> Aggiungi servizio</button>
          )}
        </div>
      </section>

      {/* AREA RISERVATA PROGRESSI */}
      <section id="progressi" className="py-32 px-6 md:px-14 lg:px-24 relative overflow-hidden" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream }} data-testid="progressi">
        {/* Decorative circles */}
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-10" style={{ backgroundColor: T_STYLE.gold }} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-5" style={{ backgroundColor: T_STYLE.gold }} />

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs tracking-[3px] uppercase" style={{ backgroundColor: `${T_STYLE.gold}20`, color: T_STYLE.gold, ...sans }}>
              <Lock className="w-3 h-3" />
              <T editing={editing} value={pr.eyebrow} onChange={(v) => set("progressi.eyebrow", v)} as="span" />
            </div>
            <h2 className="text-4xl md:text-6xl font-light leading-[1.05] mb-6" style={serif}>
              <T editing={editing} value={pr.title_line1} onChange={(v) => set("progressi.title_line1", v)} as="span" />
              <br />
              <em style={{ color: T_STYLE.goldSoft, fontStyle: "italic" }}>
                <T editing={editing} value={pr.title_line2} onChange={(v) => set("progressi.title_line2", v)} as="span" />
              </em>
            </h2>
            <T editing={editing} value={pr.subtitle} onChange={(v) => set("progressi.subtitle", v)} multiline as="p"
               className="text-lg opacity-85 max-w-3xl mx-auto font-light block" style={sans} />
          </div>

          {/* Mockup Dashboard */}
          <div className="bg-white/5 backdrop-blur-sm border rounded-lg p-6 md:p-10 shadow-2xl" style={{ borderColor: `${T_STYLE.gold}40` }}>
            {/* Metric cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {(pr.metriche || []).map((m, i) => (
                <div key={i} className="bg-white/5 border rounded p-5" style={{ borderColor: `${T_STYLE.gold}30` }} data-testid={`metrica-${i}`}>
                  <div className="text-xs opacity-70 tracking-wide uppercase mb-2" style={sans}>
                    {editing ? <Input value={m.label} onChange={(e) => { const arr = [...pr.metriche]; arr[i] = { ...m, label: e.target.value }; set("progressi.metriche", arr); }} className="bg-white/95 text-slate-900 h-6 text-xs" /> : m.label}
                  </div>
                  <div className="text-4xl font-light mb-1" style={{ color: m.colore === "gold" ? T_STYLE.gold : T_STYLE.cream, ...serif }}>
                    {editing ? <Input value={m.valore_ora} onChange={(e) => { const arr = [...pr.metriche]; arr[i] = { ...m, valore_ora: e.target.value }; set("progressi.metriche", arr); }} className="bg-white/95 text-slate-900 h-9" /> : m.valore_ora}
                    <span className="text-sm opacity-70 ml-1" style={sans}>{m.unita}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs" style={sans}>
                    <span className="opacity-60">da {editing ? <input value={m.valore_inizio} onChange={(e) => { const arr = [...pr.metriche]; arr[i] = { ...m, valore_inizio: e.target.value }; set("progressi.metriche", arr); }} className="w-10 bg-transparent border-b border-white/30 text-white" /> : m.valore_inizio}</span>
                    <TrendingUp className="w-3 h-3" style={{ color: T_STYLE.gold }} />
                    <span className="font-semibold" style={{ color: T_STYLE.gold }}>{editing ? <input value={m.trend} onChange={(e) => { const arr = [...pr.metriche]; arr[i] = { ...m, trend: e.target.value }; set("progressi.metriche", arr); }} className="w-16 bg-transparent border-b border-white/30 text-white" /> : m.trend}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Line chart mockup (SVG) */}
            <div className="bg-white/5 border rounded p-6" style={{ borderColor: `${T_STYLE.gold}30` }} data-testid="grafico-energia">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <LineChart className="w-5 h-5" style={{ color: T_STYLE.gold }} />
                  <T editing={editing} value={pr.grafico_energia?.titolo} onChange={(v) => set("progressi.grafico_energia.titolo", v)} as="span"
                     className="text-sm font-semibold" style={sans} />
                </div>
                <span className="text-xs opacity-60" style={sans}>Simulazione media utenti</span>
              </div>
              <svg viewBox="0 0 700 240" className="w-full h-56" xmlns="http://www.w3.org/2000/svg">
                {/* Grid horizontal lines */}
                {[0, 25, 50, 75, 100].map((v, i) => (
                  <g key={i}>
                    <line x1="40" y1={220 - (v * 1.9)} x2="680" y2={220 - (v * 1.9)} stroke={T_STYLE.gold} strokeOpacity="0.1" strokeWidth="1" />
                    <text x="8" y={225 - (v * 1.9)} fill={T_STYLE.cream} fillOpacity="0.5" fontSize="10" style={sans}>{v}</text>
                  </g>
                ))}
                {/* Line + area */}
                {(() => {
                  const punti = pr.grafico_energia?.punti || [];
                  const labels = pr.grafico_energia?.labels || [];
                  if (punti.length === 0) return null;
                  const stepX = 640 / (punti.length - 1 || 1);
                  const pts = punti.map((v, i) => `${40 + i * stepX},${220 - (v * 1.9)}`).join(" ");
                  const areaPts = `40,220 ${pts} ${40 + (punti.length - 1) * stepX},220`;
                  return (
                    <>
                      <polygon points={areaPts} fill={T_STYLE.gold} fillOpacity="0.15" />
                      <polyline points={pts} fill="none" stroke={T_STYLE.gold} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                      {punti.map((v, i) => (
                        <g key={i}>
                          <circle cx={40 + i * stepX} cy={220 - (v * 1.9)} r="5" fill={T_STYLE.forest} stroke={T_STYLE.gold} strokeWidth="2.5" />
                          <text x={40 + i * stepX} y={235} fill={T_STYLE.cream} fillOpacity="0.7" fontSize="11" textAnchor="middle" style={sans}>{labels[i] || `M${i}`}</text>
                          <text x={40 + i * stepX} y={220 - (v * 1.9) - 12} fill={T_STYLE.gold} fontSize="11" fontWeight="600" textAnchor="middle" style={sans}>{v}</text>
                        </g>
                      ))}
                    </>
                  );
                })()}
              </svg>
            </div>

            {/* Note privacy + CTA */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs opacity-70 flex items-center gap-2" style={sans}>
                <Lock className="w-3 h-3" style={{ color: T_STYLE.gold }} />
                <T editing={editing} value={pr.note_privacy} onChange={(v) => set("progressi.note_privacy", v)} as="span" />
              </p>
              <a href={pr.cta_href} className="inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02]"
                 style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest, ...sans }} data-testid="progressi-cta">
                <Lock className="w-3 h-3" />
                <T editing={editing} value={pr.cta_label} onChange={(v) => set("progressi.cta_label", v)} as="span" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SHOP */}
      <section id="shop" className="py-32 px-6 md:px-14 lg:px-24" data-testid="shop">
        <div className="max-w-6xl mx-auto text-center mb-16">
          <T editing={editing} value={sh.eyebrow} onChange={(v) => set("shop.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
          <T editing={editing} value={sh.title} onChange={(v) => set("shop.title", v)} as="h2"
             className="text-4xl md:text-6xl font-light mb-6 block" style={{ color: T_STYLE.forest, ...serif }} />
          <T editing={editing} value={sh.subtitle} onChange={(v) => set("shop.subtitle", v)} multiline as="p"
             className="text-lg max-w-2xl mx-auto font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-6">
          {(sh.prodotti || []).map((p, i) => (
            <div key={i} className="group" data-testid={`prodotto-${i}`}>
              <div className="relative overflow-hidden mb-4" style={{ aspectRatio: "1/1", backgroundColor: T_STYLE.line }}>
                <img src={p.immagine} alt={p.titolo} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" loading="lazy" />
                {editing && (
                  <button onClick={() => uploadImage(`shop.prodotti[${i}].immagine`)} className="absolute top-2 right-2 text-xs px-2 py-1 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest }}>Img</button>
                )}
                <div className="absolute bottom-3 left-3 text-xs px-3 py-1 uppercase tracking-wider font-semibold" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream, ...sans }}>
                  {editing ? <Input value={p.categoria} onChange={(e) => { const arr = [...sh.prodotti]; arr[i] = { ...p, categoria: e.target.value }; set("shop.prodotti", arr); }} className="text-slate-900 h-6 w-32 text-xs" /> : p.categoria}
                </div>
              </div>
              <T editing={editing} value={p.titolo} onChange={(v) => { const arr = [...sh.prodotti]; arr[i] = { ...p, titolo: v }; set("shop.prodotti", arr); }} as="h3"
                 className="text-lg font-normal mb-1 block" style={{ color: T_STYLE.forest, ...serif }} />
              <div className="flex items-center justify-between mb-3">
                <span className="text-base" style={{ color: T_STYLE.forest, ...sans }}>
                  {editing ? <Input value={p.prezzo} onChange={(e) => { const arr = [...sh.prodotti]; arr[i] = { ...p, prezzo: e.target.value }; set("shop.prodotti", arr); }} className="h-7 w-20" /> : p.prezzo}
                </span>
              </div>
              <button disabled className="w-full py-2 text-xs tracking-[2px] uppercase font-semibold border cursor-not-allowed opacity-60" style={{ borderColor: T_STYLE.forest, color: T_STYLE.forest, ...sans }}>Prossimamente</button>
              {editing && (
                <button onClick={() => { const arr = sh.prodotti.filter((_, ii) => ii !== i); set("shop.prodotti", arr); }} className="mt-2 text-xs text-red-500 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Rimuovi</button>
              )}
            </div>
          ))}
          {editing && (
            <button onClick={() => set("shop.prodotti", [...(sh.prodotti || []), { titolo: "Nuovo prodotto", categoria: "Categoria", prezzo: "€ 0", immagine: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=800&q=80" }])} className="border-2 border-dashed p-8 flex flex-col items-center justify-center min-h-[300px]" style={{ borderColor: T_STYLE.gold, color: T_STYLE.gold }}><Plus className="w-8 h-8 mb-2" /> Aggiungi prodotto</button>
          )}
        </div>
      </section>

      {/* RETREAT */}
      <section id="retreat" className="relative h-[600px] w-full overflow-hidden" data-testid="retreat">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${rt.image})` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${T_STYLE.forest}f0 0%, ${T_STYLE.forest}80 60%, transparent 100%)` }} />
        {editing && (
          <button onClick={() => uploadImage("retreat.image")} className="absolute top-4 right-4 z-30 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest }}>Cambia sfondo</button>
        )}
        <div className="relative z-10 h-full flex items-center px-6 md:px-14 lg:px-24">
          <div className="max-w-2xl">
            <T editing={editing} value={rt.eyebrow} onChange={(v) => set("retreat.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
            <h2 className="text-white text-4xl md:text-6xl font-light leading-[1.05] mb-8" style={serif}>
              <T editing={editing} value={rt.title_line1} onChange={(v) => set("retreat.title_line1", v)} as="span" />
              <br />
              <em style={{ color: T_STYLE.goldSoft, fontStyle: "italic" }}>
                <T editing={editing} value={rt.title_line2} onChange={(v) => set("retreat.title_line2", v)} as="span" />
              </em>
            </h2>
            <T editing={editing} value={rt.description} onChange={(v) => set("retreat.description", v)} multiline as="p"
               className="text-white/85 text-lg mb-8 font-light block" style={sans} />
            <a href="#contatti" className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02]"
               style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest, ...sans }} data-testid="retreat-cta">
              <T editing={editing} value={rt.cta_label} onChange={(v) => set("retreat.cta_label", v)} as="span" /> <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="py-32 px-6 md:px-14 text-center" style={{ backgroundColor: T_STYLE.cream }} data-testid="cta-finale">
        <div className="max-w-3xl mx-auto">
          <T editing={editing} value={cta.eyebrow} onChange={(v) => set("cta_finale.eyebrow", v)} as="p"
             className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
          <h2 className="text-5xl md:text-7xl font-light leading-[1.05] mb-8" style={{ color: T_STYLE.forest, ...serif }}>
            <T editing={editing} value={cta.title_line1} onChange={(v) => set("cta_finale.title_line1", v)} as="span" />
            <br />
            <em style={{ color: T_STYLE.gold, fontStyle: "italic" }}>
              <T editing={editing} value={cta.title_line2} onChange={(v) => set("cta_finale.title_line2", v)} as="span" />
            </em>
          </h2>
          <T editing={editing} value={cta.subtitle} onChange={(v) => set("cta_finale.subtitle", v)} multiline as="p"
             className="text-lg mb-10 font-light block" style={{ color: T_STYLE.softInk, ...sans }} />
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={cta.cta_primary_href} className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02]"
               style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream, ...sans }} data-testid="cta-final-primary">
              <T editing={editing} value={cta.cta_primary} onChange={(v) => set("cta_finale.cta_primary", v)} as="span" /> <ArrowRight className="w-4 h-4" />
            </a>
            <a href={cta.cta_secondary_href} className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold border transition-colors hover:bg-black/5"
               style={{ borderColor: T_STYLE.forest, color: T_STYLE.forest, ...sans }} data-testid="cta-final-secondary">
              <T editing={editing} value={cta.cta_secondary} onChange={(v) => set("cta_finale.cta_secondary", v)} as="span" />
            </a>
          </div>
        </div>
      </section>

      {/* 1.16 TEASER AZIENDE */}
      {data.teaser_aziende && (
        <section className="relative py-20 md:py-28 px-6 md:px-14 lg:px-24 overflow-hidden" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream }} data-testid="teaser-aziende">
          <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
            backgroundImage: `radial-gradient(circle at 15% 20%, ${T_STYLE.gold}55, transparent 55%), radial-gradient(circle at 85% 80%, ${T_STYLE.goldSoft}44, transparent 55%)`
          }} />
          <div className="relative max-w-5xl mx-auto text-center">
            <T editing={editing} value={data.teaser_aziende.eyebrow} onChange={(v) => set("teaser_aziende.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.goldSoft, ...sans }} />
            <h2 className="text-4xl md:text-6xl font-light leading-[1.05] mb-6" style={serif}>
              <T editing={editing} value={data.teaser_aziende.title_line1} onChange={(v) => set("teaser_aziende.title_line1", v)} as="span" />
              <br />
              <em style={{ color: T_STYLE.goldSoft, fontStyle: "italic" }}>
                <T editing={editing} value={data.teaser_aziende.title_line2} onChange={(v) => set("teaser_aziende.title_line2", v)} as="span" />
              </em>
            </h2>
            <T editing={editing} value={data.teaser_aziende.subtitle} onChange={(v) => set("teaser_aziende.subtitle", v)} multiline as="p"
               className="text-base md:text-lg leading-relaxed opacity-85 max-w-3xl mx-auto mb-10 font-light block" style={sans} />
            <a href={data.teaser_aziende.cta_href || "/longevity/aziende"}
               className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-bold transition-all hover:scale-[1.02] shadow-2xl"
               style={{ backgroundColor: T_STYLE.gold, color: "#fff", ...sans }} data-testid="btn-teaser-aziende">
              <T editing={editing} value={data.teaser_aziende.cta_label} onChange={(v) => set("teaser_aziende.cta_label", v)} as="span" /> <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      )}

      {/* CONTATTI */}
      <section id="contatti" className="py-24 px-6 md:px-14 lg:px-24" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream }} data-testid="contatti">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16">
          <div>
            <T editing={editing} value={ct.eyebrow} onChange={(v) => set("contatti.eyebrow", v)} as="p"
               className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T_STYLE.gold, ...sans }} />
            <T editing={editing} value={ct.title} onChange={(v) => set("contatti.title", v)} as="h2"
               className="text-4xl md:text-5xl font-light mb-6 block" style={serif} />
            <T editing={editing} value={ct.subtitle} onChange={(v) => set("contatti.subtitle", v)} multiline as="p"
               className="text-base opacity-85 mb-6 font-light block" style={sans} />
            {ct.corporate_link_label && (
              <a href={ct.corporate_link_href || "/longevity/aziende"}
                 className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline"
                 style={{ color: T_STYLE.goldSoft }} data-testid="link-corporate-in-contatti">
                <T editing={editing} value={ct.corporate_link_label} onChange={(v) => set("contatti.corporate_link_label", v)} as="span" />
              </a>
            )}
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3" style={sans}>
                <Mail className="w-4 h-4" style={{ color: T_STYLE.gold }} />
                {editing ? <Input value={ct.email} onChange={(e) => set("contatti.email", e.target.value)} className="bg-white/95 text-slate-900" /> : <a href={`mailto:${ct.email}`} className="hover:underline">{ct.email}</a>}
              </div>
              <div className="flex items-center gap-3" style={sans}>
                <Phone className="w-4 h-4" style={{ color: T_STYLE.gold }} />
                {editing ? <Input value={ct.phone} onChange={(e) => set("contatti.phone", e.target.value)} className="bg-white/95 text-slate-900" /> : <span>{ct.phone}</span>}
              </div>
              <div className="flex items-center gap-3" style={sans}>
                <MapPin className="w-4 h-4" style={{ color: T_STYLE.gold }} />
                {editing ? <Input value={ct.indirizzo} onChange={(e) => set("contatti.indirizzo", e.target.value)} className="bg-white/95 text-slate-900" /> : <span>{ct.indirizzo}</span>}
              </div>
            </div>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); toast.success("Grazie! Ti contatteremo a breve."); }} className="space-y-4" data-testid="contact-form">
            <Input placeholder="Nome e cognome" required className="bg-white/95 text-slate-900 h-12" style={sans} />
            <Input type="email" placeholder="Email" required className="bg-white/95 text-slate-900 h-12" style={sans} />
            <Input placeholder="Telefono (opzionale)" className="bg-white/95 text-slate-900 h-12" style={sans} />
            <Textarea placeholder="Il tuo messaggio" rows={5} required className="bg-white/95 text-slate-900" style={sans} />
            <button type="submit" className="w-full py-4 text-sm tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.01]" style={{ backgroundColor: T_STYLE.gold, color: T_STYLE.forest, ...sans }}>
              Invia richiesta
            </button>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-6 md:px-14" style={{ backgroundColor: T_STYLE.ink, color: T_STYLE.cream }}>
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs opacity-80" style={sans}>
          <div className="flex items-center gap-3">
            <LongevityLogo color={T_STYLE.cream} size={28} />
            {editing ? <Input value={ft.copyright} onChange={(e) => set("footer.copyright", e.target.value)} className="bg-white/95 text-slate-900 w-80" /> : <span>{ft.copyright}</span>}
          </div>
          <div className="flex gap-6">
            {(ft.links || []).map((l, i) => (
              editing ? (
                <Input key={i} value={l.label} onChange={(e) => { const arr = [...ft.links]; arr[i] = { ...l, label: e.target.value }; set("footer.links", arr); }} className="bg-white/95 text-slate-900 h-6 w-24" />
              ) : (
                <a key={i} href={l.href} className="hover:underline">{l.label}</a>
              )
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LongevityAcademyPage;
